import { apiPut } from '@/store/api';
import { ChangeParams } from '@/types/ChangeParams';
import { GatResponseResult } from '@/types/GatResponseResult';
import type { ProformaCurrency, ProformaInfo, ProformaLine, ProformaWindow } from '@/types/ProformaTypes';

export interface ProformaLineUpdatePayload {
  /** The Proforma line ID */
  ID: number;
  /** The description */
  PRICE_TEXT: string;
  /** The quantity */
  QUANTITY_IN: number;
  /** The label for the quantity */
  QUANTITY_LABEL: string;
  /** Rebate percent. */
  REBATE_IN: number;
  /** The rebate amount. */
  REBATE_AMOUNT: number;
  /** The unit price. */
  RATE: number;
  /** The multiplier */
  MULTIPLE: number;
  /** The VAT amount in local currency */
  VAT: number;
  /** The VAT percent */
  VAT_PERCENT: number;
  /** The total amount in local currency */
  AMOUNT_IN: number;
  /** Comments */
  COMMENTS: string;
  /** Tariff description */
  TARIFF_DESCRIPTION: string;
  /** The currency amount */
  CUR1: number | null;
  /** The currency amount */
  CUR2: number | null;
  /** The currency amount */
  CUR3: number | null;
  /** The currency amount */
  CUR4: number | null;
  /** The total amount in local currency */
  BASE_AMOUNT: number;
}

export interface ProformaCurrencyUpdatePayload {
  /** The currency ID */
  PER_ID: number;
  /** The currency rate */
  PER_RATE: number;
  /** The currency amount */
  PER_SHOW: number;
}

export interface ProformaDetailsUpdatePayload
  extends Pick<
    ProformaInfo,
    | 'ID'
    | 'SUBTOTAL_IN'
    | 'COMMENTS_NEW'
    | 'CUR_CODE1'
    | 'CUR_CODE2'
    | 'CUR_CODE3'
    | 'CUR_CODE4'
    | 'DESCRIPTION'
    | 'USER_FIELD1'
  > {}

export interface UpdateProformaRequestPayload {
  proformaLines: ProformaLineUpdatePayload[];
  currencies: ProformaCurrencyUpdatePayload[];
  proformaDetails: ProformaDetailsUpdatePayload;
}

function isProformaWindowPayload(
  payload: ChangeParams<UpdateProformaRequestPayload | ProformaWindow>
): payload is ChangeParams<ProformaWindow> {
  return (
    Object.hasOwn(payload.Value, 'proformaInfo') &&
    Object.hasOwn(payload.Value, 'proformaLines') &&
    Object.hasOwn(payload.Value, 'currencies') &&
    Object.hasOwn(payload.OrgValue, 'proformaInfo') &&
    Object.hasOwn(payload.OrgValue, 'proformaLines') &&
    Object.hasOwn(payload.OrgValue, 'currencies')
  );
}

function mapProformaLineToUpdatePayload(line: ProformaLine): ProformaLineUpdatePayload {
  const rebateIn = line.REBATE_IN ?? 0;
  return {
    ID: line.ID,
    PRICE_TEXT: line.PRICE_TEXT ?? '',
    QUANTITY_IN: line.QUANTITY_IN ?? 0,
    QUANTITY_LABEL: line.QUANTITY_LABEL ?? '',
    REBATE_IN: rebateIn * -1,
    REBATE_AMOUNT: line.REBATE_AMOUNT ?? 0,
    RATE: line.RATE ?? 0,
    MULTIPLE: line.MULTIPLE ?? 1, // Default to 1 if not set
    VAT: line.VAT ?? 0, // VAT in local currency
    VAT_PERCENT: line.VAT_PERCENT ?? 0,
    AMOUNT_IN: line.AMOUNT_IN ?? 0,
    COMMENTS: line.COMMENTS ?? '',
    TARIFF_DESCRIPTION: line.TARIFF_DESCRIPTION ?? '',
    BASE_AMOUNT: line.BASE_AMOUNT ?? 0,
    CUR1: line.CUR1,
    CUR2: line.CUR2,
    CUR3: line.CUR3,
    CUR4: line.CUR4,
  };
}

function mapCurrencyToUpdatePayload(currency: ProformaCurrency): ProformaCurrencyUpdatePayload {
  return {
    PER_ID: currency.PER_ID,
    PER_RATE: currency.RATE,
    PER_SHOW: currency.PER_SHOW,
  };
}

function mapProformaDetailsToUpdatePayload(proformaDetails: ProformaInfo): ProformaDetailsUpdatePayload {
  return {
    ID: proformaDetails.ID,
    /** The total amount. */
    SUBTOTAL_IN: proformaDetails.SUBTOTAL_IN,
    COMMENTS_NEW: proformaDetails.COMMENTS_NEW,
    CUR_CODE1: proformaDetails.CUR_CODE1,
    CUR_CODE2: proformaDetails.CUR_CODE2,
    CUR_CODE3: proformaDetails.CUR_CODE3,
    CUR_CODE4: proformaDetails.CUR_CODE4,
    DESCRIPTION: proformaDetails.DESCRIPTION,
    USER_FIELD1: proformaDetails.USER_FIELD1,
    // BEAM: proformaDetails.BEAM,
    // CLIENT_ID: proformaDetails.CLIENT_ID,
    // DRAFT_ARRIVAL: proformaDetails.DRAFT_ARRIVAL,
    // DRAFT_DEPARTURE: proformaDetails.DRAFT_DEPARTURE,
    // DISCH_Q: proformaDetails.DISCH_Q,
    // GT: proformaDetails.GT,
    // HARBOUR_ID: proformaDetails.HARBOUR_ID,
    // LOADED_Q: proformaDetails.LOADED_Q,
    // LOA: proformaDetails.LOA,
    // NT: proformaDetails.NT,
    // PAX_CAP: proformaDetails.PAX_CAP,
    // QUAY_ID: proformaDetails.QUAY_ID,
    // CBM: proformaDetails.CBM,
    // VESSEL_TYPE: proformaDetails.VESSEL_TYPE,
    // CSI: proformaDetails.CSI,
    // DWT: proformaDetails.DWT,
    // SUMMERDRAFT: proformaDetails.SUMMERDRAFT,
    // VESSEL_ID: proformaDetails.VESSEL_ID,
    // ESI: proformaDetails.ESI,
    // ETA: proformaDetails.ETA,
    // ETD: proformaDetails.ETD,
    // LOCAL_CURRENCY: proformaDetails.LOCAL_CURRENCY,
    // PORTCALL_ID: proformaDetails.PORTCALL_ID,
    // PROFORMA_NUMBER: proformaDetails.PROFORMA_NUMBER,
    // SBT: proformaDetails.SBT,
    // VESSEL_NAME: proformaDetails.VESSEL_NAME,
  };
}

export async function updateProforma(
  proformaId: string,
  payload: ChangeParams<UpdateProformaRequestPayload | ProformaWindow>
) {
  let sentPayload: ChangeParams<UpdateProformaRequestPayload | ProformaWindow> | undefined = isProformaWindowPayload(
    payload
  )
    ? undefined
    : payload;

  if (!sentPayload && isProformaWindowPayload(payload)) {
    // If the payload is a ProformaWindow, we need to map it to the expected format.
    sentPayload = {
      Value: {
        proformaLines: payload.Value.proformaLines.map(mapProformaLineToUpdatePayload),
        currencies: payload.Value.currencies.map(mapCurrencyToUpdatePayload),
        proformaDetails: mapProformaDetailsToUpdatePayload(payload.Value.proformaInfo),
      },
      OrgValue: {
        proformaLines: payload.OrgValue.proformaLines.map(mapProformaLineToUpdatePayload),
        currencies: payload.OrgValue.currencies.map(mapCurrencyToUpdatePayload),
        proformaDetails: mapProformaDetailsToUpdatePayload(payload.OrgValue.proformaInfo),
      },
    };
  }
  const response = (await apiPut(`proforma/${proformaId}`, sentPayload)) as GatResponseResult<undefined>;
  return response;
}
